// Service d'authentification pour ADMINISTRATEURS
// Utilise l'API backend avec JWT tokens
// Le code secret est validé côté serveur (sécurisé)
import apiService from './apiService';

const authServiceAdmin = {
  // Inscription admin avec code secret
  register: async (userData) => {
    try {
      const response = await apiService.post('/auth/register/admin', {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        secretCode: userData.secretCode
      }, { userType: 'admin' });  // Utilise le timeout par défaut de 30s

      if (response.success) {
        apiService.setToken(response.token, 'admin');
        localStorage.setItem('currentAdmin', JSON.stringify(response.user));
        return { success: true, user: response.user };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de l\'inscription'
      };
    }
  },

  // Connexion admin — étape 1 (mot de passe).
  // Envoie le jeton d'appareil de confiance s'il existe : le backend saute
  // alors le 2FA et renvoie directement le token. Sinon { requires_2fa: true }.
  login: async (email, password) => {
    try {
      const device_token = localStorage.getItem('adminDeviceToken') || undefined;
      const response = await apiService.post('/auth/login/admin', {
        email,
        password,
        device_token
      }, { userType: 'admin' });

      if (response.success && response.requires_2fa) {
        return { success: true, requires_2fa: true };
      }
      if (response.success && response.token) {
        apiService.setToken(response.token, 'admin');
        localStorage.setItem('currentAdmin', JSON.stringify(response.user));
        return { success: true, user: response.user };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de la connexion'
      };
    }
  },

  // Connexion admin — étape 2 (code reçu par e-mail).
  verify2FA: async (email, code, trustDevice) => {
    try {
      const response = await apiService.post('/auth/login/admin/verify', {
        email,
        code,
        trust_device: !!trustDevice
      }, { userType: 'admin' });

      if (response.success && response.token) {
        apiService.setToken(response.token, 'admin');
        localStorage.setItem('currentAdmin', JSON.stringify(response.user));
        if (response.device_token) {
          localStorage.setItem('adminDeviceToken', response.device_token);
        }
        return { success: true, user: response.user };
      }
      return response;
    } catch (error) {
      return { success: false, error: error.error || 'Code invalide' };
    }
  },

  // Renvoi du code 2FA (cooldown 60 s côté serveur).
  resend2FA: async (email) => {
    try {
      return await apiService.post('/auth/login/admin/resend', { email }, { userType: 'admin' });
    } catch (error) {
      return { success: false, error: error.error || 'Renvoi impossible' };
    }
  },

  // Déconnexion
  logout: () => {
    localStorage.removeItem('currentAdmin');
    apiService.removeToken('admin');
    return { success: true };
  },

  // Vérifier si connecté
  isAuthenticated: () => {
    return !!apiService.getToken('admin') && !!localStorage.getItem('currentAdmin');
  },

  // Récupérer l'admin actuel
  getCurrentUser: () => {
    const admin = localStorage.getItem('currentAdmin');
    return admin ? JSON.parse(admin) : null;
  },

  // Mettre à jour son profil (nom seulement : l'e-mail ne se change que par un superadmin)
  updateProfile: async ({ name }) => {
    try {
      const currentAdmin = authServiceAdmin.getCurrentUser();
      if (!currentAdmin) {
        return { success: false, error: 'Non connecté' };
      }

      const response = await apiService.patch('/admin/me', { name }, { userType: 'admin' });

      if (response.success) {
        const updatedAdmin = { ...currentAdmin, name: response.admin?.name ?? name };
        localStorage.setItem('currentAdmin', JSON.stringify(updatedAdmin));
        return { success: true, user: updatedAdmin };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de la mise à jour'
      };
    }
  },

  // Changer son mot de passe (vérifie l'actuel côté serveur ; oublie les appareils de confiance)
  changePassword: async (currentPassword, newPassword) => {
    try {
      if (!authServiceAdmin.getCurrentUser()) {
        return { success: false, error: 'Non connecté' };
      }

      const response = await apiService.put(
        '/admin/me/password',
        { currentPassword, newPassword },
        { userType: 'admin' }
      );
      // Le serveur a oublié les appareils de confiance : ce jeton ne sert plus.
      if (response?.success) localStorage.removeItem('adminDeviceToken');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors du changement de mot de passe'
      };
    }
  }
};

export default authServiceAdmin;